import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { TruckIcon, ShieldCheck, Lock, CheckCircle } from 'lucide-react';
import '../styles/modern.css';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // TODO: Implement remember me functionality (currently unused)
  // const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)' }}>
      {/* Left Side - Branding & Imagery */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
        position: 'relative'
      }}>
        {/* Geometric Pattern Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '12px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <TruckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Transportation Safety</h1>
              <p className="text-blue-100 text-sm">Platform</p>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4 leading-tight">
                Comprehensive fleet safety management
              </h2>
              <p className="text-xl text-blue-100">
                Monitor compliance, track performance, and ensure safety across your entire transportation network.
              </p>
            </div>

            {/* Trust Signals */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Real-time Compliance Monitoring</p>
                  <p className="text-sm text-blue-100">Track DOT, FMCSA, and safety metrics in real-time</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Automated Violation Detection</p>
                  <p className="text-sm text-blue-100">AI-powered analysis of driver and vehicle data</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">Enterprise-Grade Security</p>
                  <p className="text-sm text-blue-100">Bank-level encryption and SOC 2 Type II certified</p>
                </div>
              </div>
            </div>

            {/* Social Proof */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '20px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <p className="text-sm text-blue-100 mb-2">Trusted by leading carriers</p>
              <p className="font-semibold text-lg">"This platform transformed our safety operations"</p>
              <p className="text-sm text-blue-100 mt-2">- Director of Safety, National Freight Lines</p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm text-blue-100">
            {new Date().getFullYear()} Transportation Safety Platform. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex justify-center items-center gap-3 mb-2">
              <div style={{
                background: 'var(--color-primary)',
                padding: '10px',
                borderRadius: '10px'
              }}>
                <TruckIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-gray-900)' }}>
                Transportation Safety
              </h1>
            </div>
          </div>

          {/* Login Card */}
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: '48px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--color-gray-200)'
          }} className="fade-in-up">
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-gray-900)' }}>
                Welcome back
              </h2>
              <p className="text-muted">
                Sign in to access your safety dashboard
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6">
                <Alert type="danger" message={error} onClose={() => setError('')} />
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold mb-2"
                  style={{ color: 'var(--color-gray-700)' }}
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease',
                    color: 'var(--color-gray-900)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold mb-2"
                  style={{ color: 'var(--color-gray-700)' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease',
                    color: 'var(--color-gray-900)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-gray-300)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                {/* TODO: Implement remember me functionality - placeholder for future
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: 'var(--color-primary)'
                    }}
                  />
                  <span className="text-sm" style={{ color: 'var(--color-gray-600)' }}>
                    Remember me
                  </span>
                </label>
                */}
                <div />
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                style={{
                  width: '100%',
                  height: '48px',
                  fontSize: '15px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? (
                  <>
                    <LoadingSpinner message="Signing in..." size="sm" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Sign in securely</span>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'var(--color-gray-200)'
                }} />
                <div className="relative text-center">
                  <span style={{
                    background: 'white',
                    padding: '0 12px',
                    fontSize: '13px',
                    color: 'var(--color-gray-500)'
                  }}>
                    New to the platform?
                  </span>
                </div>
              </div>

              {/* Sign Up Link */}
              <div className="text-center">
                <Link
                  to="/register"
                  style={{
                    display: 'inline-block',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--color-gray-50)';
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                  }}
                >
                  Create an account
                </Link>
              </div>
            </form>

            {/* Test Account Info */}
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'var(--color-gray-500)' }}>
              <Link to="/privacy" style={{ color: 'var(--color-gray-600)' }}>
                Privacy Policy
              </Link>
              <span>•</span>
              <Link to="/terms" style={{ color: 'var(--color-gray-600)' }}>
                Terms of Service
              </Link>
              <span>•</span>
              <Link to="/contact" style={{ color: 'var(--color-gray-600)' }}>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
